import { Button, Loader, Theme } from '@busybusy/webapp-react-ui';
import { AxiosResponse } from 'axios';
import classNames from 'classnames';
import { HeaderDialog, Panel } from 'components';
import Graylog from 'components/domain/analytics/Graylog/Graylog';
import PageProgressIndicator from 'components/foundation/PageProgressIndicator/PageProgressIndicator';
import useLogin from 'containers/login/hooks/useLogin';
import { isWithinSevenDays } from 'containers/login/utils/utils';
import { detect } from 'detect-browser';
import {
  useActiveMember,
  useActiveMemberUpdate,
  useFirstRender,
  useOpenable,
  useOrganizationInfoUpdate,
  useReduxSelector,
} from 'hooks';
import useOrganizationSettingsGlobalUpdate from 'hooks/models/organization/settings/useOrganizationSettingsGlobalUpdate/useOrganizationSettingsGlobalUpdate';
import useCreateOrganizationPayPeriodOvertime from 'hooks/useCreateOrganizationPayPeriodOvertime';
import useOrganizationPayPeriod from 'hooks/useOrganizationPayPeriod';
import { sha256 } from 'js-sha256';
import { isEmpty, isNil } from 'lodash';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import createSignUpEventRestService from 'services/rest/signUpEventRestService';
import createRpcService from 'services/rpc/rpcService';
import { IStoreEnvironment } from 'store/types';
import { IMember } from 'types';
import IBusyApiWrapper from 'types/BusyApiWrapper';
import { ClassName } from 'types/ClassName';
import { IRestOrganizationInfo } from 'types/OrganizationInfo';
import { ICreateSignUpEvent } from 'types/SignUpEvent';
import CompanySize from 'types/enum/CompanySize';
import { DayOfWeekValue } from 'types/enum/DayOfWeek';
import { PayPeriodTypeValue } from 'types/enum/PayPeriodType';
import Trade from 'types/enum/Trade';
import { getNonAuthenticatedAxiosClient } from 'utils/axiosClient';
import AnalyticsEvent from 'utils/constants/analytics/analyticsEvent';
import { QueryParam } from 'utils/constants/queryParam';
import { timeStampUtc } from 'utils/dateUtils';
import { getQueryParam, getQueryParamByType, parse, stringify } from 'utils/queryStringUtils';
import { getFullNameFromMember } from 'utils/stringUtils';
import { getOsVersion } from 'utils/userEnvironment';
import { uuid } from 'utils/uuidUtils';
import OnboardingPage from '../OnboardingPage/OnboardingPage';
import OwnerOnboardingAccountPage, {
  IOnboardingAccountData,
} from './OwnerOnboardingAccountPage/OwnerOnboardingAccountPage';
import OwnerOnboardingBusinessNeedsPage, {
  BusinessNeedsTitleRecord,
  IBusinessNeedsOnboardingData,
} from './OwnerOnboardingBusinessNeedsPage/OwnerOnboardingBusinessNeedsPage';
import OwnerOnboardingCompanyPage, {
  ICompanyOnboardingData,
  ICompanyOnboardingData as IOnboardingCompanyData,
} from './OwnerOnboardingCompanyPage/OwnerOnboardingCompanyPage';
import './OwnerOnboardingContainer.scss';
import OwnerOnboardingProfilePage, { IProfileData } from './OwnerOnboardingProfilePage/OwnerOnboardingProfilePage';
import OwnerOnboardingCompletedPage from './OwnerOnboardingSuccessPage/OwnerOnboardingSuccessPage';
import OwnerOnboardingTimeTrackingPage, {
  ITimeTrackingOnboardingData,
  TimeTrackTitleRecord,
} from './OwnerOnboardingTimeTrackingPage/OwnerOnboardingTimeTrackingPage';

declare global {
  // tslint:disable-next-line: interface-name
  interface Window {
    growsumo: any;
  }
}

interface IPartnerStackReferral {
  organizationInfo: IRestOrganizationInfo;
  data: IOnboardingData;
}

export interface IOwnerOnboardingContainerProps {
  className?: ClassName;
}

interface IOnboardingData {
  companyData: IOnboardingCompanyData;
  accountData: IOnboardingAccountData;
  profileData: IProfileData;
  businessNeedsData: IBusinessNeedsOnboardingData;
  timeTrackingData: ITimeTrackingOnboardingData;
  token?: string;
}

const OwnerOnboardingContainer: FunctionComponent<IOwnerOnboardingContainerProps> = (props) => {
  const { className } = props;
  const navigate = useNavigate();
  const { search } = useLocation();
  const botDialogOpenable = useOpenable();

  const [t] = useTranslation();
  const queryParams = parse(search);
  const activeMember = useActiveMember();
  const isAuthenticated = useReduxSelector((state) => state.session.isAuthenticated);
  const [currentPage, setCurrentPage] = useState(1);
  const [onboardingData, setOnboardingData] = useState<IOnboardingData>({
    accountData: {
      username: getQueryParam(queryParams, QueryParam.USERNAME),
      password: '',
      confirmPassword: '',
      email: getQueryParam(queryParams, QueryParam.EMAIL),
      companyName: getQueryParam(queryParams, QueryParam.COMPANY_NAME),
    },
    profileData: {
      firstName: getQueryParam(queryParams, QueryParam.FIRST_NAME),
      lastName: getQueryParam(queryParams, QueryParam.LAST_NAME),
      phone: getQueryParam(queryParams, QueryParam.PHONE),
    },
    companyData: {
      companySize: getQueryParamByType(queryParams, QueryParam.COMPANY_SIZE, CompanySize, ''),
      trade: getQueryParamByType(queryParams, QueryParam.TRADE, Trade, ''),
      payrollData: {
        weeklyStartDay: getQueryParamByType(
          queryParams,
          QueryParam.PAY_PERIOD_START,
          DayOfWeekValue,
          DayOfWeekValue.MONDAY
        ),
        payPeriodType: getQueryParamByType(
          queryParams,
          QueryParam.PAY_PERIOD_TYPE,
          PayPeriodTypeValue,
          PayPeriodTypeValue.WEEKLY
        ),
        overtimeStart: getQueryParamByType(
          queryParams,
          QueryParam.OVERTIME_START,
          DayOfWeekValue,
          DayOfWeekValue.MONDAY
        ),
        biWeeklyPayPeriodDay: getQueryParam(queryParams, QueryParam.BI_WEEKLY_PAY_PERIOD_DAY),
        monthlyDay: getQueryParam(queryParams, QueryParam.MONTHLY_DAY, '1st'),
        semiMonthlyDays: [
          getQueryParam(queryParams, QueryParam.SEMI_MONTHLY_DAY_ONE, '1st'),
          getQueryParam(queryParams, QueryParam.SEMI_MONTHLY_DAY_TWO, '16th'),
        ],
      },
    },
    businessNeedsData: {
      jobCosting: false,
      photoDocumentation: false,
      budgets: false,
      documentSharing: false,
      equipment: false,
      scheduling: false,
      fieldReports: false,
      buildTrustAndVerify: false,
      improvePayroll: false,
      softwareIntegrations: false,
    },
    timeTrackingData: {
      onPhone: false,
      supervisors: false,
      digitalKiosk: false,
    },
  });

  const [accountCreatedOpen, setAccountCreatedOpen] = useState(false);
  const [accountCreateSpinnerOpen, setAccountCreateSpinnerOpen] = useState(false);
  const environment = useSelector((state: { environment: IStoreEnvironment }) => state.environment);
  const login = useLogin();
  const client = getNonAuthenticatedAxiosClient(environment);
  const rpcService = createRpcService(client);
  const [, updateMember] = useActiveMemberUpdate();
  const [, updateOrgInfo] = useOrganizationInfoUpdate();
  const { createPayPeriod } = useOrganizationPayPeriod();
  const { createPayPeriodOvertime } = useCreateOrganizationPayPeriodOvertime();
  const autoTrial = useRef(queryParams.start_trial ? true : false);
  const publicKey = useRef<string | null>(null);
  const memberId = useRef<string | null>(null);
  const hasFeaturesListEnabled = true;
  const updateOrganizationGlobalSettings = useOrganizationSettingsGlobalUpdate();
  const isFirstRender = useFirstRender();

  const sessionIdentifier = useRef<string>(uuid());

  useEffect(() => {
    Graylog({
      shortMsg: AnalyticsEvent.onboarding.ONBOARDING_START,
      logObject: {
        _identifier: sessionIdentifier.current,
        _event_type: 'v4-0',
        _is_property_1: isNil(hasFeaturesListEnabled) ? false : hasFeaturesListEnabled,
      },
    });
  }, []);

  useEffect(() => {
    if (isFirstRender && isAuthenticated) {
      if (
        activeMember.firstName === 'John' &&
        activeMember.lastName === 'Doe' &&
        isNil(activeMember.phone) &&
        isWithinSevenDays(activeMember.organization!.createdOn) &&
        activeMember.id === activeMember.organization!.ownedBy
      ) {
        setCurrentPage(2);
        return;
      } else {
        navigate('/');
        return;
      }
    }
  }, []);

  function graylogCreateAccount(accountData: IOnboardingAccountData) {
    Graylog({
      shortMsg: AnalyticsEvent.onboarding.ONBOARDING_CREATE_ACCOUNT,
      logObject: {
        _identifier: sessionIdentifier.current,
        _company_name: accountData.companyName,
        _email: accountData.email,
        _event_type: 'v4-0',
        _is_property_1: isNil(hasFeaturesListEnabled) ? false : hasFeaturesListEnabled,
      },
    });
  }

  function graylogPayPeriodScheduleViewed() {
    Graylog({
      shortMsg: AnalyticsEvent.onboarding.ONBOARDING_PAY_PERIOD_SCHEDULE_VIEWED,
      logObject: {
        _identifier: sessionIdentifier.current,
        _event_type: 'v4-0',
        _is_property_1: isNil(hasFeaturesListEnabled) ? false : hasFeaturesListEnabled,
      },
    });
  }

  function graylogPayPeriodScheduleChanged() {
    Graylog({
      shortMsg: AnalyticsEvent.onboarding.ONBOARDING_PAY_PERIOD_SCHEDULE_CHANGED,
      logObject: {
        _identifier: sessionIdentifier.current,
        _event_type: 'v4-0',
        _is_property_1: isNil(hasFeaturesListEnabled) ? false : hasFeaturesListEnabled,
      },
    });
  }

  function graylogAccountProfile(profileData: IProfileData) {
    Graylog({
      shortMsg: AnalyticsEvent.onboarding.ONBOARDING_ACCOUNT_PROFILE,
      logObject: {
        _identifier: sessionIdentifier.current,
        _first_name: profileData.firstName,
        _last_name: profileData.lastName,
        _phone: profileData.phone,
        _event_type: 'v4-0',
        _is_property_1: isNil(hasFeaturesListEnabled) ? false : hasFeaturesListEnabled,
      },
    });
  }

  function graylogCompanyInformation(companyData: ICompanyOnboardingData) {
    Graylog({
      shortMsg: AnalyticsEvent.onboarding.ONBOARDING_COMPANY_INFORMATION,
      logObject: {
        _identifier: sessionIdentifier.current,
        _company_size: companyData.companySize,
        _trade: companyData.trade,
        _event_type: 'v4-0',
        _is_property_1: isNil(hasFeaturesListEnabled) ? false : hasFeaturesListEnabled,
      },
    });
  }

  function graylogFeatureList(businessNeedsData?: IBusinessNeedsOnboardingData) {
    Graylog({
      shortMsg: AnalyticsEvent.onboarding.ONBOARDING_FEATURE_LIST,
      logObject: {
        _identifier: sessionIdentifier.current,
        _member_id: activeMember.id,
        _selection: businessNeedsData
          ? Object.entries(businessNeedsData)
              .filter((entry) => entry[1])
              .map((entry) => entry[0])
          : [],
        _event_type: 'v4-0',
      },
    });
  }

  function graylogTimeTracked(timeTrackingData: ITimeTrackingOnboardingData) {
    Graylog({
      shortMsg: AnalyticsEvent.onboarding.ONBOARDING_TIME_TRACKED,
      logObject: {
        _identifier: sessionIdentifier.current,
        _member_id: activeMember.id,
        _selection: Object.entries(timeTrackingData)
          .filter((entry) => entry[1])
          .map((entry) => entry[0]),
        _event_type: 'v4-0',
      },
    });
  }

  function graylogCompleted() {
    Graylog({
      shortMsg: AnalyticsEvent.onboarding.ONBOARDING_COMPLETED,
      logObject: {
        _identifier: sessionIdentifier.current,
        _event_type: 'v4-0',
        _is_property_1: isNil(hasFeaturesListEnabled) ? false : hasFeaturesListEnabled,
      },
    });
  }

  function renderCurrentPage() {
    switch (currentPage) {
      case 1:
        return renderAccountPage();
      case 2:
        return renderProfilePage();
      case 3:
        return renderCompanyPage();
      case 4:
        return renderBusinessNeedsPage();
      case 5:
        return renderTimeTrackingPage();
    }
  }

  function getSignupEvent(): ICreateSignUpEvent {
    const gclid = getQueryParam(queryParams, 'gclid', undefined);
    let prefix = 'utm_';

    if (gclid) {
      prefix = 'busy_';
    }

    const source = getQueryParam(queryParams, `${prefix}source`, undefined);
    const medium = getQueryParam(queryParams, `${prefix}medium`, undefined);
    const campaign = getQueryParam(queryParams, `${prefix}campaign`, undefined);
    const term = getQueryParam(queryParams, `${prefix}term`, undefined);
    const content = getQueryParam(queryParams, `${prefix}content`, undefined);
    const referralCode = getQueryParam(queryParams, 'rsCode', undefined);

    const browserInfo = detect();

    const os = browserInfo ? browserInfo.os : undefined;
    const osVersion = getOsVersion();
    const browser = browserInfo ? browserInfo.name : undefined;
    const browserVersion = browserInfo ? browserInfo.version : undefined;

    const device = `${os ? os : ''} ${osVersion ? osVersion : ''} - ${browser ? browser : ''} ${
      browserVersion ? browserVersion : ''
    }`;

    const event: ICreateSignUpEvent = {
      ad_click_date: timeStampUtc(),
      member_id: memberId.current ? memberId.current : '',
      created_on: timeStampUtc(),
      platform: 'web',
      os: os ? os : undefined,
      os_version: osVersion,
      app_version: process.env.REACT_APP_VERSION,
      device,
    };

    if (source) {
      event.source = source;
    }

    if (medium) {
      event.medium = medium;
    }

    if (campaign) {
      event.campaign = campaign;
    }

    if (content) {
      event.content = content;
    }

    if (term) {
      event.term = term;
    }

    const nonAttributionParams = [
      QueryParam.EMAIL,
      QueryParam.USERNAME,
      QueryParam.COMPANY_NAME,
      QueryParam.COMPANY_SIZE,
      QueryParam.TRADE,
      QueryParam.PAY_PERIOD_START,
      QueryParam.PAY_PERIOD_TYPE,
      QueryParam.OVERTIME_START,
      QueryParam.BI_WEEKLY_PAY_PERIOD_DAY,
      QueryParam.SEMI_MONTHLY_DAY_ONE,
      QueryParam.SEMI_MONTHLY_DAY_TWO,
      QueryParam.FIRST_NAME,
      QueryParam.LAST_NAME,
      QueryParam.PHONE,
    ];

    const entries = Object.entries(queryParams).filter(([key]) => {
      return !nonAttributionParams.includes(key) && key !== 'rsCode';
    });

    const attribution = JSON.stringify({ ...Object.fromEntries(entries), rsCode: referralCode });

    return { ...event, attribution_data: attribution };
  }

  async function createAccount(data: IOnboardingData) {
    setAccountCreateSpinnerOpen(true);

    const hashedPassword = sha256(data.accountData.password);

    const response = await rpcService.account.create({
      username: data.accountData.username ? data.accountData.username.trim() : data.accountData.email.trim(),
      email: data.accountData.email,
      password: hashedPassword,
      organization_name: data.accountData.companyName,
      first_name: 'John',
      last_name: 'Doe',
      app_type: 'web',
      token: data.accountData.token ?? '',
    });

    if (isEmpty(response.data.errors)) {
      return finishAccountSetup(data, response.data.result.data, hashedPassword);
    } else {
      setAccountCreateSpinnerOpen(false);
      if (response.data.errors[0].code === 207) {
        botDialogOpenable.open();
      }
    }
  }

  async function updateAccount(data: IOnboardingData) {
    const memberResponse = updateMember({
      firstName: data.profileData.firstName,
      lastName: data.profileData.lastName,
      phone: data.profileData.phone,
    });

    return memberResponse;
  }

  async function updateCompany(data: IOnboardingData) {
    await updateOrgInfo({
      trade: data.companyData.trade,
      employeeMin: data.companyData.companySize
        ? (getCompanySizeExtrema(data.companyData.companySize)[0] ?? undefined)
        : undefined,
      employeeMax: data.companyData.companySize
        ? (getCompanySizeExtrema(data.companyData.companySize)[1] ?? undefined)
        : undefined,
    });
  }

  function renderAccountPage() {
    return <OwnerOnboardingAccountPage onAccountSubmit={onAccountPageSubmit} formData={onboardingData.accountData} />;
  }

  function onAccountPageSubmit(accountData: IOnboardingAccountData) {
    graylogCreateAccount(accountData);
    const data = { ...onboardingData, accountData };
    setOnboardingData(data);
    createAccount(data);
  }

  function renderProfilePage() {
    return (
      <OwnerOnboardingProfilePage formData={onboardingData.profileData} onProfileFormSubmit={onProfilePageSubmit} />
    );
  }

  async function onProfilePageSubmit(profileData: IProfileData) {
    graylogAccountProfile(profileData);
    const data = { ...onboardingData, profileData };
    setOnboardingData(data);
    await updateAccount(data);
    setCurrentPage(3);
  }

  function renderCompanyPage() {
    return (
      <OwnerOnboardingCompanyPage
        onCompanyFormSubmit={onCompanyPageSubmit}
        formData={onboardingData.companyData}
        onPayPeriodScheduleViewed={graylogPayPeriodScheduleViewed}
        onPayPeriodScheduleChanged={graylogPayPeriodScheduleChanged}
      />
    );
  }

  function onCompanyPageSubmit(companyData: IOnboardingCompanyData) {
    graylogCompanyInformation(companyData);
    const data = { ...onboardingData, companyData };
    setOnboardingData(data);
    updateCompany(data);
    createPayPeriod(companyData.payrollData);
    createPayPeriodOvertime(companyData.payrollData);
    if (hasFeaturesListEnabled) {
      setCurrentPage(4);
    } else {
      setAccountCreatedOpen(true);
    }
  }

  function getCompanySizeExtrema(companySize: CompanySize) {
    switch (companySize) {
      case CompanySize.ONE_TO_FOUR:
        return [1, 4];
      case CompanySize.FIVE_TO_TWENTY:
        return [5, 20];
      case CompanySize.TWENTY_ONE_TO_FIFTY:
        return [21, 50];
      case CompanySize.FIFTY_ONE_TO_ONE_FIFTY:
        return [51, 150];
      case CompanySize.ONE_FIFTY_ONE_PLUS:
        return [151, null];
    }
  }

  async function onBusinessNeedsPageSubmit(businessNeedsData?: IBusinessNeedsOnboardingData) {
    graylogFeatureList(businessNeedsData);
    if (!isNil(businessNeedsData)) {
      const listOfFeatures = Object.keys(businessNeedsData)
        .filter((key) => businessNeedsData[key as keyof IBusinessNeedsOnboardingData])
        .map((key) => BusinessNeedsTitleRecord[key as keyof IBusinessNeedsOnboardingData]);
      await updateOrganizationGlobalSettings('onboardingFeatureSelections', listOfFeatures);
      setOnboardingData({ ...onboardingData, businessNeedsData });
    }
    setCurrentPage(5);
  }

  function renderBusinessNeedsPage() {
    return (
      <OwnerOnboardingBusinessNeedsPage
        formData={onboardingData.businessNeedsData}
        onBusinessNeedsFormSubmit={onBusinessNeedsPageSubmit}
      />
    );
  }

  async function onTimeTrackingPageSubmitted(timeTrackingData: ITimeTrackingOnboardingData) {
    graylogTimeTracked(timeTrackingData);
    const listOfFeatures = Object.keys(timeTrackingData)
      .filter((key) => timeTrackingData[key as keyof ITimeTrackingOnboardingData])
      .map((key) => TimeTrackTitleRecord[key as keyof ITimeTrackingOnboardingData]);
    await updateOrganizationGlobalSettings('onboardingHowTrackTimeSelections', listOfFeatures);
  }

  async function onPremiumTrialDialogDismissed() {
    setAccountCreatedOpen(true);
  }

  function renderTimeTrackingPage() {
    return (
      <OwnerOnboardingTimeTrackingPage
        formData={onboardingData.timeTrackingData}
        onTimeTrackingFormSubmit={onTimeTrackingPageSubmitted}
        onPremiumTrialDialogDismissed={onPremiumTrialDialogDismissed}
      />
    );
  }

  function renderPager() {
    return (
      <PageProgressIndicator
        progressLabels={[t('Account'), t('Profile'), t('Company Setup'), t('Business Needs'), t('Time Tracking')]}
        currentPage={currentPage}
      />
    );
  }

  function onBackClick() {
    if (currentPage > 1) {
      // Clear password if they navigated back
      if (currentPage === 4) {
        setOnboardingData({
          ...onboardingData,
          accountData: {
            ...onboardingData.accountData,
            password: '',
            confirmPassword: '',
          },
        });
      }
      setCurrentPage(currentPage - 1);
    } else {
      navigate(-1);
    }
  }

  function renderPage() {
    return (
      <>
        <Loader isOpen={accountCreateSpinnerOpen} label={t('Creating account...')} overlay={true} theme={Theme.DARK} />
        <OnboardingPage pager={renderPager()} onBackClick={onBackClick} hideBackClick={isAuthenticated}>
          {renderCurrentPage()}
        </OnboardingPage>
        <HeaderDialog
          isOpen={botDialogOpenable.isOpen}
          onClose={botDialogOpenable.close}
          title={t('Account Flagged')}
          divider={false}
        >
          <div className="px-6 py-2">
            <p>{t('This account has been flagged as a robot. Please try again later.')}</p>
          </div>
          <Button className="m-4" type="secondary" onClick={botDialogOpenable.close}>
            {t('OK')}
          </Button>
        </HeaderDialog>
      </>
    );
  }

  function getMember(username: string, password: string) {
    return client.get('/member', {
      auth: { password, username },
      params: { username, username_unique: true },
    });
  }

  async function finishAccountSetup(
    data: IOnboardingData,
    returnedData: { member: IMember; organization_info: IRestOrganizationInfo } | null,
    hashedPassword: string
  ) {
    if (returnedData?.member && returnedData?.organization_info) {
      try {
        await partnerStackReferral({
          organizationInfo: returnedData.organization_info,
          data,
        });
      } catch (e) {
        // log the error for debugging, but otherwise ignore it
        // eslint-disable-next-line no-console
        console.error(`Error in growsumo.createSignup `, e);
      }
    }

    getMember(
      isEmpty(data.accountData.username) ? data.accountData.email : data.accountData.username!,
      hashedPassword
    ).then((res: AxiosResponse<IBusyApiWrapper<IMember>>) => {
      if (res.data.data && res.data.data.length > 0) {
        publicKey.current = res.data.public_key;
        memberId.current = res.data.data[0].id;
        accountCreateModelUpdates()
          .then(() => {
            setCurrentPage(2);
            setAccountCreateSpinnerOpen(false);
          })
          .catch(() => {
            setCurrentPage(2);
            setAccountCreateSpinnerOpen(false);
          });
      }
    });
  }

  // https://docs.partnerstack.com/docs/partnerstackjs#step-2-track-signups
  async function partnerStackReferral({ organizationInfo, data }: IPartnerStackReferral) {
    window.growsumo.data.name = [data.profileData.firstName, data.profileData.lastName].join(' ');
    window.growsumo.data.email = data.accountData.email;
    window.growsumo.data.customer_key = organizationInfo.organization_id;

    return new Promise((resolve, reject) => {
      try {
        window.growsumo.createSignup(resolve);
      } catch (e) {
        reject(e);
      }
    });
  }

  function accountCreateModelUpdates() {
    const promises: Array<Promise<any>> = [];
    const headers = { headers: { 'key-authorization': publicKey.current ?? '' } };

    if (autoTrial.current && environment.REACT_APP_ENV !== 'localhost') {
      promises.push(rpcService.subscriptionRpc.startTrial(headers));
    }

    createSignUpEventRestService(client).post(getSignupEvent(), headers);

    login(memberId.current!, publicKey.current!);

    return Promise.all(promises);
  }

  function onExploreAppClick() {
    if (memberId.current && publicKey.current) {
      login(memberId.current, publicKey.current);
    } else {
      navigate({ pathname: '/login' });
    }
  }

  function getScheduleDemoHref() {
    let phone = onboardingData.profileData.phone;
    if (!phone.startsWith('1') && !phone.startsWith('+1')) {
      phone = '1' + phone;
    }

    const params = stringify({
      name: getFullNameFromMember(onboardingData.profileData),
      email: onboardingData.accountData.email,
      a1: phone,
      a2: onboardingData.accountData.companyName,
      utm_campaign: 'Onboarding_CTA',
      utm_medium: 'In_App',
      utm_source: 'Web',
    });

    return `https://calendly.com/busybusy-walkthrough/20min?${params}`;
  }

  function renderCompleted() {
    graylogCompleted();
    return (
      <OwnerOnboardingCompletedPage
        className="ac-fade-slide-up"
        firstName={onboardingData.profileData.firstName}
        onExploreAppClick={onExploreAppClick}
        scheduleDemoHref={getScheduleDemoHref()}
      />
    );
  }

  const classes = classNames('owner-onboarding-container', 'ac-fade-in-place', className);

  return <Panel className={classes}>{accountCreatedOpen ? renderCompleted() : renderPage()}</Panel>;
};

export default OwnerOnboardingContainer;
